import {inject, Injectable} from '@angular/core';
import {BaseService} from "./BaseService";
import {GraphicUtils} from "@aiknow-srl/aik-ngx-common";
import {GlobalModelService} from "./GlobalModel.service";
import {PostLoginCB} from "../ope/user/PostLoginCB";
import {MenuGroup} from "../models/user/MenuGroup";
import {MenuVoice} from "../models/user/MenuVoice";
import {NotificationService} from "./notification/notification.service";
import {UserMetricInstance} from "../models/user/Metric/UserMetricInstance";
import {DataTypeGroupEnum} from "../enums/dashboard/DataTypeUnitEnum";
import {environment} from "../../environments/environment";
import Clarity from '@microsoft/clarity';

@Injectable({
  providedIn: 'root'
})
export class AppService extends BaseService {

  readonly APP_NAME = "Keton Cloud";
  readonly APP_CHANNEL = "WEB";
  readonly PASSWORD_RESET_ACTION = 'pwReset';
  readonly EMAIL_PATTERN = '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$';
  readonly EMAIL_REGEX = new RegExp(this.EMAIL_PATTERN);
  readonly PHONE_REGEX = /^[+]?[0-9 ()-]+$/i;
  // ****************************
  /**
   * Mobile screen width threshold
   */
  public readonly MOBILE_SCREEN_THRESHOLD = 570;
  /**
   * Small screen width threshold
   */
  public readonly SMALL_SCREEN_THRESHOLD = 850;
  /**
   * Medium screen width threshold
   */
  public readonly MEDIUM_SCREEN_THRESHOLD = 1200;
  /**
   * Big screen width threshold
   */
  public readonly BIG_SCREEN_THRESHOLD = 1800;
  // ****************************
  public anyRegistryEntityChanged: boolean = false;
  public readonly IUBENDA_PRIVACY_POLICY_LINK = 'https://www.iubenda.com/privacy-policy/32671768';
  public readonly CACHE_NAME = "keton-cache";
  private readonly gmService: GlobalModelService = inject(GlobalModelService);
  private readonly notificationService = inject(NotificationService);

  constructor() {
    super();
  }

  private _menuVoice?: MenuVoice;

  // getter and setter for _menuVoice, same as _menuGroup
  public get menuVoice() {
    return this._menuVoice;
  }

  public set menuVoice(value) {
    this._menuVoice = value;
  }

  private _menuGroup?: MenuGroup;

  // ****************************
  // RESPONSIVE THRESHOLD PARAMS

  public get menuGroup() {
    return this._menuGroup;
  }

  public set menuGroup(value) {
    this._menuGroup = value;
  }

  private _isDarkTheme?: boolean;

  get isDarkTheme(): any {
    return this._isDarkTheme;
  }

  // ****************************
  //      REGISTRY PARAMS

  set isDarkTheme(value: boolean) {
    this._isDarkTheme = value;
  }

  // ----------------------------------
  //     GLOBAL APP METHODS
  // ----------------------------------

  /**
   * The method prints AIknow infos in the console
   */
  printStartingAppInfosInConsole() {

    this.logger.info("\n" +
      "         ((((((#(   (((((((#          \n" +
      "        ((((((###. (((((((###         \n" +
      "       ((((((#####,((((((#####        \n" +
      "      (((((( #####(((((( ######       \n" +
      "     ((((((   ###((((((   ######      \n" +
      "    ((((((     #((((((     ######     \n" +
      "    ######     ((((((#     ((((((     \n" +
      "     ######   ((((((###   ((((((      \n" +
      "      ###### ((((((##### ((((((       \n" +
      "       #####((((((*#####((((((        \n" +
      "        ###((((((( ####((((((         \n" +
      "         #(((((((   ##((((((          \n" +
      "     AIknow - Developing future       \n" +
      "\t Contact: \t" + "support@aiknow.io     \n" +
      "\t Website: \t" + "https://aiknow.io     \n" +
      "\t App Name: \t" + this.APP_NAME + "           \n");

  }

  printMenuVoiceName() {
    if (this.menuGroup)
      return 'MENU_VOICE.' + this.menuGroup?.name + '.' + this.menuVoice?.name.toUpperCase();
    else
      return 'MENU_VOICE.NO_GROUP.' + this.menuVoice?.name.toUpperCase();
  }

  /**
   * THe method stores the PostLoginCB parameters into the GlobalModelService and logs some info data
   * @param cb
   */
  storeParamsIntoGlobalModel(cb: PostLoginCB): void {
    this.gmService.storeParamsIntoGlobalModel(cb);

    this.logger.info("\n\t App ver: \t" + this.gmService.getAppVersion() + "         \n" +
      "\t App env: \t" + this.gmService.envIdLabel + "\n" +
      "\t User: \t\t" + this.gmService.user.username + "    \n");

    this.notificationService.manageMqttConnection();
    if (environment.clarityProjectId) {
      const user = this.gmService.user;
      Clarity.identify(String(user.id), undefined, undefined, user.username);
    }
  }


  // *******************************
  //   RESPONSIVE UTILITY METHODS
  // *******************************


  isMobileScreen() {
    return GraphicUtils.isScreenWidthSmaller(this.MOBILE_SCREEN_THRESHOLD);
  }

  isSmallScreen() {
    return GraphicUtils.isScreenWidthSmaller(this.SMALL_SCREEN_THRESHOLD);
  }

  isMediumScreen() {
    return GraphicUtils.isScreenWidthSmaller(this.MEDIUM_SCREEN_THRESHOLD);
  }

  isBigScreen() {
    return GraphicUtils.isScreenWidthSmaller(this.BIG_SCREEN_THRESHOLD);
  }

  /**
   * Generates a deep copy of the item parameter.
   * If it can't, it returns undefined.
   * @param item Item to copy
   */
  deepCopy<T>(item: T): T {
    return structuredClone(item);
  }

  customLabelMethod(field: string, data: any) {
    let target = field + '.' + data.text;
    let label = this.translate.instant(target);
    if (label === target || !label)
      return target;
    return label
  }

  customHintMethod(field: string, data: any) {
    let target = field + '.HELP_TEXTS.' + data.text;
    let tooltip = this.translate.instant(target);
    if (tooltip === target || !tooltip)
      return false;
    return tooltip
  }

  setRegistryEntityIsChanged(entityChanged: boolean) {
    this.anyRegistryEntityChanged = entityChanged;
  }

  getRegistryEntityIsChanged() {
    return this.anyRegistryEntityChanged;
  }

  deepEquals(object1: any, object2: any) {
    const keys1 = Object.keys(object1);
    const keys2 = Object.keys(object2);

    if (keys1.length !== keys2.length) {
      return false;
    }

    for (const key of keys1) {
      const val1 = object1[key];
      const val2 = object2[key];
      const areObjects = this.isObject(val1) && this.isObject(val2);
      if (
        areObjects && !this.deepEquals(val1, val2) ||
        !areObjects && val1 !== val2
      ) {
        return false;
      }
    }

    return true;
  }

  isObject(object: any) {
    return object != null && typeof object === 'object';
  }

  getMaxCharErrorMessage(maxChar: number) {
    return this.translate.instant("ERROR.CHARS_OVER_MAX", [maxChar]);
  }

  cleanupServiceFields(): void {
    this.menuVoice = undefined;
    this.menuGroup = undefined;
  }

  translateMaybeBoolean(maybeBoolean: boolean | number | string) {
    if (typeof maybeBoolean === "boolean") {
      if (maybeBoolean)
        return this.translate.instant("COMMON.TRUE");
      else
        return this.translate.instant("COMMON.FALSE");
    } else if (typeof maybeBoolean === "string") {
      if (maybeBoolean === "true")
        return this.translate.instant("COMMON.TRUE");
      else
        return this.translate.instant("COMMON.FALSE");
    } else if (typeof maybeBoolean === "number") {
      if (maybeBoolean === 1)
        return this.translate.instant("COMMON.TRUE");
      else
        return this.translate.instant("COMMON.FALSE");
    } else if (maybeBoolean === null) {
      return "---";
    }
  }

  getParsedMetricValue(measure: UserMetricInstance) {
    if ((measure.dataTypeGroup === DataTypeGroupEnum.FLOAT || measure.dataTypeGroup === DataTypeGroupEnum.INTEGER)
      && typeof measure.lastValue === "number") {
      if (!!measure.lastValue || measure.lastValue === 0) {
        if (measure.dataTypeGroupShowAsHex) {
          return "0x" + Math.round(measure.lastValue).toString(16).toUpperCase();
        } else {
          return measure.lastValue.toFixed(measure.decimalDigits?? 0)
        }
      } else {
        return "---"
      }
    } else if (measure.dataTypeGroup === DataTypeGroupEnum.BOOLEAN) {
      return this.translateMaybeBoolean(measure.lastValue)
    } else {
      if (measure.lastValue)
        return measure.lastValue;
      return "---"
    }
  }
}
